import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, CheckCircle2, PhoneCall } from "lucide-react";
import { submitLead, PHONE_ONLY_EMAIL_PLACEHOLDER } from "@/lib/submit-lead";
import { useSpamProtection } from "@/hooks/use-spam-protection";
import { trackEvent } from "@/lib/analytics";

interface Props {
  /** Recorded on the lead so you can see which page the request came from. */
  source?: string;
  className?: string;
  heading?: string;
  intro?: string;
  compact?: boolean;
}

/**
 * Low-friction consultation request: three fields, no multi-step intake.
 * Lands in the leads table as a family_care lead and notifies the team inbox
 * through the shared submitLead pipeline.
 */
const QuickConsultForm = ({
  source = "inline_consult",
  className = "",
  heading = "Not sure where to start?",
  intro =
    "Tell us a little about your loved one and our Nashville team will reach out with practical, no-pressure guidance.",
  compact = false,
}: Props) => {
  const { validate, honeypotName } = useSpamProtection();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);

    const spamCheck = validate(data);
    if (!spamCheck.ok) {
      toast.error(
        spamCheck.reason === "honeypot"
          ? "Something went wrong. Please try again."
          : "Please take a moment to complete the form before submitting.",
      );
      return;
    }

    const fullName = ((data.get("full_name") as string) || "").trim();
    const contact = ((data.get("contact") as string) || "").trim();
    const situation = ((data.get("situation") as string) || "").trim();

    const isEmail = contact.includes("@");
    if (!isEmail && contact.replace(/\D/g, "").length < 10) {
      toast.error("Please enter a valid email address or phone number.");
      return;
    }

    setLoading(true);
    try {
      await submitLead({
        full_name: fullName,
        email: isEmail ? contact : PHONE_ONLY_EMAIL_PLACEHOLDER,
        phone: isEmail ? "" : contact,
        lead_type: "family_care",
        message: situation
          ? `Quick consultation request: ${situation}`
          : "Quick consultation request (no details provided).",
        lead_source: source,
        lead_source_details: typeof window !== "undefined" ? window.location.pathname : undefined,
      });
      setDone(true);
      trackEvent("quick_consult_submit", { source });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        className={`rounded-2xl border border-border bg-card p-6 text-center ${className}`}
        role="status"
      >
        <CheckCircle2 className="h-8 w-8 text-primary mx-auto mb-3" aria-hidden="true" />
        <h3 className="text-lg font-heading font-semibold mb-1">Thank you — we have your note</h3>
        <p className="text-muted-foreground text-sm">
          A member of our team will reach out personally, usually the same business day. If it's
          urgent, call us at (615) 807-0618.
        </p>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl border border-border bg-card p-6 md:p-8 ${className}`}>
      <div className={compact ? "mb-4" : "mb-5"}>
        <span className="inline-flex items-center gap-2 text-xs font-medium text-primary mb-2">
          <PhoneCall className="h-3.5 w-3.5" aria-hidden="true" />
          Private consultation
        </span>
        <h3 className="text-xl md:text-2xl font-heading font-semibold">{heading}</h3>
        <p className="text-muted-foreground mt-1 text-sm md:text-base">{intro}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="hidden" aria-hidden="true">
          <label htmlFor={`${source}-website`}>Website</label>
          <input id={`${source}-website`} name={honeypotName} tabIndex={-1} autoComplete="off" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor={`${source}-name`}>Your name</Label>
            <Input id={`${source}-name`} name="full_name" required maxLength={200} autoComplete="name" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${source}-contact`}>Phone or email</Label>
            <Input
              id={`${source}-contact`}
              name="contact"
              required
              maxLength={255}
              placeholder="(615) 555-0123 or you@email.com"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={`${source}-situation`}>
            A little about your loved one and your area{" "}
            <span className="text-muted-foreground font-normal">(optional)</span>
          </Label>
          <Textarea
            id={`${source}-situation`}
            name="situation"
            rows={3}
            maxLength={1500}
            placeholder="Mom lives alone in Green Hills and needs company and rides to appointments."
          />
        </div>

        <Button type="submit" size="lg" className="w-full sm:w-auto" disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />
              Sending…
            </>
          ) : (
            "Request a private consultation"
          )}
        </Button>
        <p className="text-xs text-muted-foreground">
          No cost, no obligation. We're a non-medical companion care practice serving Nashville and
          nearby towns.
        </p>
      </form>
    </div>
  );
};

export default QuickConsultForm;
